<script setup>
import { computed, ref, onMounted, nextTick, watch } from 'vue';


/* ───────────────── props (read-only) ───────────────── */
const props = defineProps({
  isServerReconnectModalOpen: Boolean,
  displayAlert: { type: Function, required: true },
  login: { type: Function, required: true },
  logout: { type: Function, required: true },
  tagEvent: { type: Function, required: true },
  toggleModals: { type: Function, required: true }
});

/* ───────────────── local state ───────────────── */
const showFields = ref(false);
const email = ref('');
const password = ref('');
const errorMessage = ref('');
const forgot = ref(false);

const emailInputRef = ref(null);
const passwordInputRef = ref(null);

/* ───────────────── helpers ───────────────── */
function raf() {
  return new Promise(r => requestAnimationFrame(r));
}

/* ───────────────── autofill logic ───────────────── */
async function checkAutofillAndLogin() {
  const emailVal = document.getElementById('reconnect-email')?.value || email.value || '';
  const passwordVal = document.getElementById('reconnect-password')?.value || password.value || '';

  console.log("Autofill values:", { emailVal, passwordVal });

  // Autofill detected → submit form
  if (emailVal && passwordVal) {
    email.value = emailVal;
    password.value = passwordVal;
    submitForm();
    return;
  }

  // Autofill not detected → show form
  showFields.value = true;
  emailInputRef.value?.focus();
}

/* ───────────────── watchers ───────────────── */
watch(() => props.isServerReconnectModalOpen, (open) => {
  if (open) {
    showFields.value = true;
    window.setTimeout(() => {
      document.querySelector('#reconnect')?.click();
      raf().then(() => raf()).then(() => raf()).then(() => {
        document.getElementById('reconnect-email')?.click();
        document.getElementById('reconnect-email')?.focus();
      });
      nextTick().then(() => {
        console.log("Checking for autofill...");
        checkAutofillAndLogin();
      });
    }, 500);
  }
});

/* ───────────────── form actions ───────────────── */
function submitForm() {
  if (forgot.value) forgotPassword();
  else sendData();
}

async function sendData() {
  errorMessage.value = '';

  try {
    const serverResponse = await fetch('/api/auth/registration', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email.value,
        password: password.value
      })
    });

    const responseData = await serverResponse.json();

    if (!serverResponse.ok || responseData.status === 400 || responseData.statusText === "Bad Request" || responseData.success === false) {
      const messageText = "Couldn't log you in. Please check your email and password and try again.";
      props.displayAlert('', messageText, 'toast');
      return;
    }

    console.log("Login successful:", responseData);
    props.login(responseData.user);

    // alert user
    const messageText = 'Signed In';
    props.displayAlert('', messageText, 'toast');
    props.tagEvent({ where: 'authentication', what: 'sign_in' });

    closeModal();
  } catch (error) {
    errorMessage.value = error.message;
    console.error("Login error:", error);
  }
}

async function forgotPassword() {
  errorMessage.value = '';

  try {
    const response = await fetch('/api/auth/forgot', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email.value,
				returnUrl: 'designexternship.com',
    		service: 'study'
      })
    });

    if (!response.ok) {
      // if (error = no user) { suggest to try a different email }
      throw new Error(`Something went wrong (error ${response.status})`);
    }

    props.displayAlert(
      'Message Sent',
      [
        "Please check your email.",
        "If the message doesn't arrive within a few minutes, you can try again or contact us at hello@brownjuice.co"
      ],
      '',
      'Okay'
    );

    forgot.value = false;
    closeModal();
  } catch (error) {
    errorMessage.value = error.message;
    console.error("Server returned error:", error);
  }
}


/* ───────────────── modal helpers ───────────────── */
function onDialogMouseDown(event) {
  // Check if click is on the dialog backdrop (not inside the modal content)
  if (event.target === event.currentTarget) {
    workOffline()
  }
}

function workOffline() {
  closeModal();
  props.displayAlert('', 'You are now working offline. Your progress will be saved locally until you reconnect.', 'toast');

  const user = appStorage.get('user');
  if (user && typeof user === 'object' && '_id' in user) {
    appStorage.set('user', { ...user, _id: '' });
  }
}

function showForgot() {
  errorMessage.value = '';
  forgot.value = true;
}

function backToMain() {
  errorMessage.value = '';
  forgot.value = false;
}

function dontReconnectToServer() {
  closeModal();
  props.logout();
  props.displayAlert('', 'You are now logged out.', 'toast');
}

function closeModal() {
  const dialog = document.getElementById('server-reconnect');
  dialog?.close();
  dialog?.setAttribute('data-open', false);
  props.toggleModals();
}
</script>

<template>
<dialog
  id="server-reconnect"
  @mousedown="onDialogMouseDown"
  :data-open="props.isServerReconnectModalOpen"
>
  <header class="text-center">
    <div v-if="forgot">
      <h2 class="my-0">Forgot your password?</h2>
      <p>We'll send you a link to reset it.</p>
    </div>
    <div v-else>
      <div v-if="showFields">
        <h2 class="my-0">Session Expired</h2>
        <p>Log back in to sync your progress</p>
      </div>
      <h2 v-else class="pad-loading-label">Loading...</h2>
    </div>
  </header>
  <form
    id="reconnect"
    @submit.prevent="submitForm()"
    :class="{loading: !showFields}"
    method="dialog"
  >
    <label>
      <span>Email</span>
      <input
        ref="emailInputRef"
        v-model="email"
        type="email"
        name="email"
        autocomplete="email"
        id="reconnect-email"
        autofocus="true"
        required
        />
    </label>
    <label v-if="!forgot">
      <div class="flex-row">
        <span>Password</span>
        <a @click="showForgot" class="link" type="button">Forgot?</a>
      </div>
      <input
        ref="passwordInputRef"
        v-model="password"
        type="password"
        name="password"
        autocomplete="current-password"
        id="reconnect-password"
        required
      />
    </label>
    <p v-if="errorMessage" style="color: var(--text-warning)">{{ errorMessage }}</p>
    <div v-if="forgot">
      <button type="submit" class="btn primary wide my-0">Send Reset Email</button>
      <button type="button" @click="backToMain" class="btn wide secondary">Back</button>
    </div>
    <div v-if="!forgot">
      <button type="submit" class="btn primary wide">Reconnect</button>
      <button @click="workOffline" class="btn secondary wide" type="button">Work Offline</button>
      <button @click="dontReconnectToServer" class="btn secondary wide" type="button">Log Out</button>
    </div>
  </form>
</dialog>
</template>

<style lang="scss" scoped>
#server-reconnect .text-center {
  text-align: center;
}
#server-reconnect .pad-loading-label {
  margin-top: 120px;
  margin-bottom: 120px;
}
#server-reconnect {
  border: none;
  background-color: var(--bg1);
  color: var(--text1);
  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  outline: none!important;

  margin: 0;
  overflow: auto;
  padding: 24px;

  max-width: 100vw;
  min-width: 340px;
  max-height: -webkit-fill-available;
  min-height: fit-content;
  @media(prefers-color-scheme:dark) {
    background-color: var(--bg3);
  }
}

/* FORM STYLES */
#server-reconnect h2 + p { margin-top: 4px; }
#server-reconnect .loading {
  // opacity: 1;
  // height: 0.1px;
  // overflow: clip;
  // pointer-events: none;
}
#server-reconnect form label {
  display: block;
  margin-bottom: 12px;
}
#server-reconnect form label span {
  display: block;
  margin-bottom: 8px;
}
#server-reconnect .flex-row {
  display: flex;
  flex-flow: row;
  justify-content: space-between;
}

@media(min-width:768px) {
  #server-reconnect {
    justify-content: space-around;
    width: fit-content;
    height: calc(100vh - 48px); // fallback
    height: calc(100dvh - 48px);
    min-height: unset;
  }
}
@media(min-width:768px) AND (min-height:480px) {
  #server-reconnect {
    border: 1px solid var(--border);
    border-radius: 24px;
    box-shadow: 0 30px 200px 40px rgba(0, 0, 0, 0.15), 0 30px 50px -30px rgba(0, 0, 0, 0.4), 0 30px 80px -50px rgba(0, 0, 0, 0.5);
    margin: calc((100vh - 432px) / 2) auto; // fallback
    margin: calc((100dvh - 432px) / 2) auto;

    justify-content: flex-start;
    height: fit-content;
    min-height: unset;
    max-height: 454px;
  }
}
</style>
